import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import { useReveal } from "../motion/useReveal";
import { useLanguage } from "../i18n/LanguageContext";
import Navbar from "../sections/Navbar";
import Hero from "../sections/Hero";
import MarqueeRibbon from "../sections/MarqueeRibbon";
import Services from "../sections/Services";
import AuditTool from "../sections/AuditTool";
import Quiz from "../sections/Quiz";
import Nachtrust from "../sections/Nachtrust";
import Work from "../sections/Work";
import About from "../sections/About";
import Contact from "../sections/Contact";
import Footer from "../sections/Footer";

// FAQ + Service entities for Answer Engine Optimisation. Questions chosen
// for the queries an LLM is most likely to receive about a Dutch web agency:
// pricing, scope, and "is dit voor mij?".
const FAQ_NL = [
  {
    q: "Wat kost een WordPress onderhoudsplan bij Ghio?",
    a: "Nachtrust onderhoudsplannen starten bij €49 per maand (Basic), €99 (Pro) en €199 (Premium). Daarin zitten updates, dagelijkse back-ups, beveiligingsmonitoring en — vanaf Pro — maandelijkse content-uren.",
  },
  {
    q: "Wat is het verschil tussen SEO en AEO?",
    a: "SEO (Search Engine Optimization) zorgt dat je website gevonden wordt door Google en andere zoekmachines. AEO (Answer Engine Optimization) zorgt dat AI-assistenten als ChatGPT, Claude en Perplexity je website correct citeren als bron. Beide zijn nodig in 2026 — Ghio doet allebei.",
  },
  {
    q: "Hoe werkt de gratis Website scan?",
    a: "Je vult je URL, naam en e-mail in. De scan haalt je pagina op zoals een browser hem krijgt, leest robots.txt, sitemap en twee menu-pagina's, en laat Google PageSpeed de snelheid meten. Claude van Anthropic legt die gemeten feiten uit in gewone taal. Binnen ongeveer een minuut heb je je rapport, met een knop om een gesprek te plannen.",
  },
  {
    q: "Werkt Ghio Webservices ook buiten Friesland?",
    a: "Ja. We zitten in Lemmer maar werken internationaal — voor klanten in heel Nederland, België, Duitsland en daarbuiten. Het meeste verloopt op afstand; voor onderhoud en remote support gebruiken we TeamViewer QuickSupport.",
  },
  {
    q: "Kan ik mijn Nachtrust-abonnement pauzeren?",
    a: "Ja. In het klantportaal kun je je abonnement pauzeren voor één of meerdere kalendermaanden, of definitief opzeggen. Er zijn geen contractuele lock-ins.",
  },
];

const faqSchema = (qa) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: qa.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export default function HomePage() {
  const { lang } = useLanguage();
  const { hash } = useLocation();
  useReveal([]);
  // Prerendered HTML has no sections yet, so the native #fragment jump misses; scroll after mount.
  useEffect(() => {
    if (!hash) return;
    const id = requestAnimationFrame(() => {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: "instant", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [hash]);
  return (
    <div data-testid="home-page">
      <SeoHead
        lang={lang}
        nlPath="/"
        enPath="/en/"
        title={
          lang === "en"
            ? "Ghio Webservices · Website. Marketing. Automations. Handled."
            : "Ghio Webservices · Website. Marketing. Automations. Geregeld."
        }
        description={
          lang === "en"
            ? "Lemmer-based digital agency building modern funnels that humans and AI actually find. Strategy, code, SEO/AEO, WordPress maintenance — all in one place."
            : "Ghio Webservices uit Lemmer bouwt moderne funnels die door mensen én AI gevonden worden. Strategie, code, SEO, AEO en WordPress-onderhoud — in één hand."
        }
        jsonLd={faqSchema(FAQ_NL)}
      />
      <Navbar />
      <main>
        <Hero />
        <MarqueeRibbon />
        <Services />
        <AuditTool />
        <Quiz />
        <Nachtrust />
        <Work />
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
